import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { APIs } from '../../utils/constants';
import { Form, Input } from 'antd';
import ModalWithAlert from './modal';
import Button from '../button';
import Text from '../text';

const EditPrompt = forwardRef((_, ref) => {
    // States
    const [form] = Form.useForm();
    const [modalTitleId, setModalTitleId] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalAlerts, setModalAlerts] = useState([]);
    const [fields, setFields] = useState([]);
    const [onSuccess, setOnSuccess] = useState(null);
    const [formValid, setFormValid] = useState(false);

    // Actions
    useImperativeHandle(ref, () => ({
        showModal(data, onSuccessCallback) {
            setOnSuccess(() => onSuccessCallback);
            setModalTitleId(data ? 'modal::edit-prompt::title::edit-prompt' : 'modal::edit-prompt::title::add-prompt');
            setFields([
                { name: ['uuid'], value: data?.uuid ?? null },
                { name: ['name'], value: data?.name ?? null },
                { name: ['value'], value: data?.value ?? null }
            ]);
            setIsModalVisible(true);
        }
    }));

    const handleModalClose = () => {
        setIsModalVisible(false);
        setFields([]);
        setModalAlerts([]);
        form.resetFields();
    };

    const handleErrorAlerts = (message) => {
        setModalAlerts([
            ...modalAlerts,
            {
                type: 'error',
                content: message
            }
        ]);
    };

    const savePrompt = async ({ uuid, name, value }) => {
        const isCreate = uuid == null;
        const url = isCreate ? APIs.CREATE_PROMPT : APIs.UPDATE_PROMPT.replace(':id', uuid);
        const method = isCreate ? 'POST' : 'PUT';

        const response = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, value }),
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(result?.message || 'Operation failed');
        }
    };

    const handleModalSubmit = () => {
        form.validateFields().then(async values => {
            try {
                await savePrompt(values);
                handleModalClose();
                if (onSuccess) onSuccess();
            } catch (error) {
                handleErrorAlerts(error.message || 'Something went wrong');
            }
        });
    };

    const handleFormChange = async (_, newFields) => {
        setFields(newFields)
        setTimeout(async () => {
            try {
                await form.validateFields();
                setFormValid(true);
            } catch (errorInfo) {
                setFormValid(false);
            }
        }, 0);
    };

    return (
        <ModalWithAlert
            title={<Text id={modalTitleId} />}
            visible={isModalVisible}
            onCancel={handleModalClose}
            alerts={modalAlerts}
            footer={[
                <Button key="submit" onClick={handleModalSubmit} disabled={!formValid}>
                    <Text id="modal::edit-prompt::button::save" />
                </Button>
            ]}
        >
            <div className="text-gray mt-3">
                <Form
                    form={form}
                    fields={fields}
                    onFieldsChange={handleFormChange}
                    layout="vertical"
                >
                    <Form.Item name="uuid" hidden><Input /></Form.Item>
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please input the name!' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="value" label="Prompt" rules={[{ required: true, message: 'Please input the prompt!' }]}>
                        <Input.TextArea rows={10} />
                    </Form.Item>
                </Form>
            </div>
        </ModalWithAlert>
    );
})

export default EditPrompt;
